import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { LazyLoadImage } from "react-lazy-load-image-component";

const Card = ({ linkedin, img, name, role, css, isMobile }) => {
  return (
    <div className={`row team-border m-0 pb-0 position-relative ${css}`}>
      <div className="col-5">
        <h3 className="team-h3">{name}</h3>
        <label className="team-label">{role}</label>

        {linkedin && (
          <>
            {isMobile ? (
              <div className="text-start" style={{ padding: "5px 13px" }}>
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline d-md-none"
                >
                  <LazyLoadImage
                    effect="blur"
                    src="/images/AboutUs/icons/linkedin-icon.svg"
                    alt="linkedin"
                    className="team-img"
                  />
                </a>
              </div>
            ) : (
              <div className="team-border-button">
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-none d-md-inline me-3"
                >
                  CONNECT ON
                </a>
                <LazyLoadImage
                  effect="blur"
                  src="/images/AboutUs/icons/linkedin-icon.svg"
                  alt="linkedin"
                  className="team-img d-none d-md-inline"
                />
              </div>
            )}
          </>
        )}

        {!isMobile && (
          <LazyLoadImage
            effect="blur"
            src="/images/AboutUs/Team/Star.svg"
            alt=""
            className="img-fluid team-star-img"
            visibleByDefault
          />
        )}
      </div>

      <div className="col-7 text-end">
        <LazyLoadImage
          effect="blur"
          src={img}
          alt={name}
          className="team-member-img"
        />
      </div>
    </div>
  );
};

export default function Team() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 767);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const headingControls = useAnimation();
  const row1Controls = useAnimation();
  const row2Controls = useAnimation();

  useEffect(() => {
    if (inView) {
      // Heading animation
      headingControls.start({
        y: 0,
        opacity: 1,
        transition: { duration: 0.55, ease: "easeOut" },
      });

      if (!isMobile) {
        // 💻 Desktop animations
        row1Controls
          .start({
            y: 0,
            opacity: 1,
            transition: {
              delay: 0.2,
              duration: 0.6,
              ease: "easeOut",
              type: "spring",
              stiffness: 70,
              damping: 14,
            },
          })
          .then(() => {
            row2Controls.start({
              y: [-15, 0, -6, 0], // subtle bounce
              opacity: 1,
              transition: {
                delay: 0.1,
                duration: 0.55,
                ease: "easeOut",
              },
            });
          });
      } else {
        // 📱 Mobile animations
        row1Controls.start({
          y: 0,
          opacity: 1,
          transition: { duration: 0.55, ease: "easeOut" },
        });
        row2Controls.start({
          y: 0,
          opacity: 1,
          transition: { delay: 0.15, duration: 0.55, ease: "easeOut" },
        });
      }
    }
  }, [inView, isMobile, headingControls, row1Controls, row2Controls]);

  const data1 = [
    {
      name: "Ronak Shah",
      role: "Founder & CEO",
      img: "/images/AboutUs/Team/Ronak.png",
      linkedin:
        "https://www.linkedin.com/in/ronak-shah-4b3b3b67/?originalSubdomain=in",
    },
    {
      name: "Kinjal Shah",
      role: "Human Resource Manager",
      img: "/images/AboutUs/Team/Kinjal.png",
      linkedin:
        "https://www.linkedin.com/in/kinjal-shah-0231a0138/?originalSubdomain=in",
    },
  ];

  const data2 = [
    // {
    //   name: "Tribhuvan Singh Deora",
    //   role: "Business Development Manager",
    //   img: "/images/AboutUs/Team/TD.png",
    // },
    {
      name: "Kevin Patel",
      role: "Tech Lead .Net",
      img: "/images/AboutUs/Team/Kevin.png",
    },
    {
      name: "Ayushi Shah",
      role: "Tech Lead PHP",
      img: "/images/AboutUs/Team/Ayushi.png",
    },
  ];

  return (
    <div ref={ref} className="row py-md-5 py-2 mx-3_5-0">
      <div className="col-12 px-3">
        <motion.h2
          className="about-heading"
          initial={{ y: -40, opacity: 0 }}
          animate={headingControls}
        >
          Meet The Team
        </motion.h2>
      </div>

      {/* Row 1 */}
      <motion.div
        className="col-12 px-3"
        initial={{ y: 50, opacity: 0 }}
        animate={row1Controls}
      >
        <div className="row">
          {isMobile
            ? data1.map((item, index) => (
                <div className="col-12 my-2" key={index}>
                  <Card {...item} css="p-2 h-100" isMobile={isMobile} />
                </div>
              ))
            : data1.map((item, index) => (
                <div className="col-md-6 col-12" key={index}>
                  <Card {...item} css="p-3" />
                </div>
              ))}
        </div>
      </motion.div>

      {/* Row 2 */}
      <motion.div
        className="col-12 px-3"
        initial={{ y: 50, opacity: 0 }}
        animate={row2Controls}
      >
        <div className={`row ${isMobile ? "" : "my-4"}`}>
          {isMobile
            ? data2.map((item, index) => (
                <div className="col-12 my-2" key={index}>
                  <Card {...item} css="p-2 h-100" isMobile={isMobile} />
                </div>
              ))
            : data2.map((item, index) => (
                <div className="col-md-4 col-12" key={index}>
                  <Card
                    {...item}
                    css={`p-2 ${
                      index === 1 ? "pe-7" : index === 2 ? "pe-5" : ""
                    }`}
                  />
                </div>
              ))}
        </div>
      </motion.div>
    </div>
  );
}
